import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../layout/Header';
import LeadersSection from '../components/LeadersSection';
import AikupCard from '../components/AikupCard';
import CorporateRestructuringCAcard from '../components/CorporateRestructuringCAcard';
import EntenteZicatModal from '../components/EntenteZicatModal';
import ActionnariatScenariosModal from '../components/ActionnariatScenariosModal';
import OricomAcquisitionModal from '../components/OricomAcquisitionModal';
import MemoFiscalBZ2026Modal from '../components/MemoFiscalBZ2026Modal';
import { useAuth } from '../contexts/AuthContext';
import { Gavel, TrendingUp, Users, BarChart3 } from 'lucide-react';
import type { AikupItem } from '../data/aikupData';

interface CAItem {
  id: number;
  title: string;
  category: string;
  description: string;
  aiSuggestion: string;
  kpiScore: number;
  imageUrl?: string;
}

const comiteCAData: CAItem[] = [
  {
    id: 1,
    title: 'Restructuration Corporative BZ',
    category: 'Gouvernance',
    description: 'Analyse décisionnelle complète des scénarios de restructuration corporative et actionnariat BZ basée sur le document DFK.',
    aiSuggestion: 'Document stratégique essentiel pour le CA. Analyse holistique des 4 scénarios avec impacts par actionnaire, graphiques et mécanismes compensatoires. Aide à la décision claire et structurée.',
    kpiScore: 95,
    imageUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80'
  },
  {
    id: 2,
    title: 'Entente bonus post-vente – François Zicat',
    category: 'Ententes & engagements',
    description: 'Traçabilité de l’entente de bonus post-vente accordée à François Zicat (3 % du dividende bi-annuel jusqu’à la fin de 2025, puis 5 % pour 2026 et 2027), formalisée par courriel et suivie par le CA.',
    aiSuggestion: 'Carte dédiée aux ententes post-transaction : permet au CA de garder une trace claire, structurée et facilement accessible des engagements financiers pris lors de la sortie d’un actionnaire. Utile pour les vérifications, la conformité et la transparence avec le comité financier.',
    kpiScore: 92,
    imageUrl: 'https://images.unsplash.com/photo-1554224155-66989c705ab7?w=800&q=80'
  },
  {
    id: 4,
    title: 'Actionnariat BZ — Scénarios de transaction',
    category: 'Gouvernance',
    description: 'Vue simple des deux scénarios de transaction pour les nouveaux acheteurs : structure mixte MegaOctets/Octets ou structure unifiée via Gestion Octets, avec la CIE # en sous-groupe corporatif.',
    aiSuggestion: 'Permettre au CA d\'expliquer rapidement aux nouveaux acheteurs leur véhicule de transaction et de visualiser l\'impact organisationnel sans se perdre dans les détails juridiques.',
    kpiScore: 97,
    imageUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80'
  },
  {
    id: 5,
    title: 'Acquisition Oricom Colocation — NO-GO',
    category: 'Acquisitions & risques',
    description: 'Dossier stratégique fermé sur la reprise potentielle de la colocation Oricom Serko + Nolin : analyse Fable 5, chiffres normalisés, raisons du NO-GO, courriel de retrait et avis CA transmis à François/Richard.',
    aiSuggestion: 'Source de vérité CA pour documenter pourquoi BZ ne dépose aucune LOI : économie réelle non viable, équipe Cloud de Richard à capacité, risque Serko, mur de renouvellements, fit stratégique insuffisant et transparence FD/RD.',
    kpiScore: 99,
    imageUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80'
  },
  {
    id: 6,
    title: 'Mémo fiscal BZ 2026 — ventes d’actions',
    category: 'Fiscalité & actionnariat',
    description: 'Lecture visuelle CA du mémo fiscal de Maxime Lamoureux sur les ventes d’actions BZ, AcquisiCo, Octet, FiducieFZ, DGC/ECGC et risques IMR.',
    aiSuggestion: 'Transforme un mémo fiscal dense en carte décisionnelle: structure de transaction, actionnariat avant/après, impacts fiscaux, dates clés et sources originales PDF/courriel téléchargeables.',
    kpiScore: 98,
    imageUrl: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&q=80'
  }
];

export default function ComiteCA() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isRestructuringModalOpen, setIsRestructuringModalOpen] = useState(false);
  const [isEntenteZicatModalOpen, setIsEntenteZicatModalOpen] = useState(false);
  const [isActionnariatScenariosModalOpen, setIsActionnariatScenariosModalOpen] = useState(false);
  const [isOricomAcquisitionModalOpen, setIsOricomAcquisitionModalOpen] = useState(false);
  const [isMemoFiscalModalOpen, setIsMemoFiscalModalOpen] = useState(false);

  // Liste des emails autorisés à accéder au CA
  const emailsAutorisesCA = [
    'dany.bourassa@bzinc.ca',
    'francois.delisle@bzinc.ca',
    'eric.durand@bzinc.ca',
  ];

  // Vérifier l'accès au chargement
  useEffect(() => {
    if (!user?.email || !emailsAutorisesCA.includes(user.email.toLowerCase())) {
      navigate('/comites');
    }
  }, [user, navigate]);

  // Ne pas afficher le contenu si l'utilisateur n'est pas autorisé
  if (!user?.email || !emailsAutorisesCA.includes(user.email.toLowerCase())) {
    return null;
  }

  const handleCardClick = (item: CAItem) => {
    if (item.id === 1) {
      setIsRestructuringModalOpen(true);
    }
    if (item.id === 2) {
      setIsEntenteZicatModalOpen(true);
    }
    if (item.id === 4) {
      setIsActionnariatScenariosModalOpen(true);
    }
    if (item.id === 5) {
      setIsOricomAcquisitionModalOpen(true);
    }
    if (item.id === 6) {
      setIsMemoFiscalModalOpen(true);
    }
  };

  const leaders = [
    {
      name: 'Dany Bourassa',
      title: 'Président & DG • Président du CA',
      avatar: '/DB.png',
      vision: 'Dany préside le Conseil d\'Administration avec une vision stratégique claire : assurer la gouvernance d\'excellence de BZ, valider les décisions stratégiques majeures, superviser les risques et garantir la conformité. Il voit le CA comme l\'organe suprême de gouvernance qui aligne stratégie, conformité et création de valeur.',
      attentes: 'Dany s\'attend à ce que le CA prenne des décisions éclairées sur les enjeux stratégiques majeurs : restructurations, investissements, risques, conformité. Il veut voir chaque membre du CA utiliser les outils et analyses pour décider avec rigueur. Son objectif : que chaque réunion CA génère des décisions stratégiques durables et mesurables.',
      pagePRIsME: 'La page CA reflète notre gouvernance stratégique : restructurations, investissements, risques majeurs, conformité, ESG. C\'est notre tableau de bord décisionnel stratégique.'
    }
  ];

  // Calculer les stats
  const totalItems = comiteCAData.length;
  const avgKpiScore = Math.round(comiteCAData.reduce((sum, item) => sum + item.kpiScore, 0) / totalItems);
  const categories = Array.from(new Set(comiteCAData.map(item => item.category)));
  const highPriorityItems = comiteCAData.filter(item => item.kpiScore >= 90).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header 
        title="Conseil d'Administration (CA)" 
        subtitle="Gouvernance stratégique BZ : restructurations, investissements, risques majeurs, conformité, ESG. Décisions stratégiques éclairées pour une gouvernance d'excellence."
      />

      <div className="max-w-7xl mx-auto px-8 pb-12">
        {/* Leaders Section */}
        <LeadersSection leaders={leaders} title="Membres du Conseil d'Administration" />

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-amber-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-amber-600" />
              <p className="text-sm text-gray-600">Outils Disponibles</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalItems}</p>
            <p className="text-xs text-gray-500 mt-1">Analyses & frameworks</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <p className="text-sm text-gray-600">Score Excellence</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{avgKpiScore}%</p>
            <p className="text-xs text-gray-500 mt-1">Moyenne KPI</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <Gavel className="w-5 h-5 text-purple-600" />
              <p className="text-sm text-gray-600">Priorités</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{highPriorityItems}</p>
            <p className="text-xs text-gray-500 mt-1">Analyses critiques (90%+)</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-gray-600">Domaines</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{categories.length}</p>
            <p className="text-xs text-gray-500 mt-1">Catégories couvertes</p>
          </motion.div>
        </div>

        {/* Cards par catégorie */}
        {categories.map((category, categoryIndex) => {
          const itemsInCategory = comiteCAData.filter(item => item.category === category);
          
          return (
            <div key={category} className="mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="flex-1 h-px bg-gradient-to-r from-amber-500 to-orange-500"></div>
                <h2 className="text-2xl font-bold text-gray-800 px-4">{category}</h2>
                <div className="flex-1 h-px bg-gradient-to-l from-amber-500 to-orange-500"></div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {itemsInCategory.map((item, idx) => {
                  // Convertir CAItem en AikupItem pour AikupCard
                  const aikupItem: AikupItem = {
                    id: item.id,
                    title: item.title,
                    category: item.category,
                    description: item.description,
                    aiSuggestion: item.aiSuggestion,
                    kpiScore: item.kpiScore,
                    imageUrl: item.imageUrl,
                  };
                  
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: categoryIndex * 0.1 + idx * 0.05 }}
                    >
                      <AikupCard
                        item={aikupItem}
                        onClick={() => handleCardClick(item)}
                        onExpertClick={() => {}}
                        index={idx}
                      />
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Message motivation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl shadow-2xl p-8 text-white"
        >
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-3xl">
                ⚡
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold mb-3">Excellence Gouvernance</h3>
              <p className="text-amber-100 text-lg leading-relaxed mb-4">
                Le Conseil d'Administration BZ pilote avec <strong>rigueur stratégique et vision long terme</strong>. 
                Chaque outil de cette page génère des décisions éclairées sur les enjeux majeurs de gouvernance.
              </p>
              <p className="text-amber-100 text-lg leading-relaxed mb-4">
                Un CA sans analyses, c'est comme un capitaine sans boussole. 
                Tu navigues à l'aveugle et tu arrives où les courants te mènent.
              </p>
              <p className="text-white text-xl font-semibold">
                ⚡ Commence par <strong>Restructuration Corporative BZ</strong> - l'analyse décisionnelle complète.
              </p>
              <p className="text-amber-200 text-sm mt-3">
                ROI moyen de ces outils: +50% qualité décisionnelle, +40% alignement stratégique, -60% risques non anticipés.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modal Restructuration */}
      <CorporateRestructuringCAcard
        isOpen={isRestructuringModalOpen}
        onClose={() => setIsRestructuringModalOpen(false)}
      />

      {/* Modal Entente Zicat */}
      <EntenteZicatModal
        isOpen={isEntenteZicatModalOpen}
        onClose={() => setIsEntenteZicatModalOpen(false)}
      />

      <ActionnariatScenariosModal
        isOpen={isActionnariatScenariosModalOpen}
        onClose={() => setIsActionnariatScenariosModalOpen(false)}
      />

      <OricomAcquisitionModal
        isOpen={isOricomAcquisitionModalOpen}
        onClose={() => setIsOricomAcquisitionModalOpen(false)}
      />

      <MemoFiscalBZ2026Modal
        isOpen={isMemoFiscalModalOpen}
        onClose={() => setIsMemoFiscalModalOpen(false)}
      />
    </div>
  );
}
